// Re-measure contract maps from a layout; spine changes re-key scopes wholesale.
import { annotateContract } from "../spineInference";
import { itemsWithLayout } from "../verticalBlockingFix";
import {
  GeneratedLayout,
  Orientation,
  PATH_SEPARATOR,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
  effectiveAttrs,
} from "../studioTypes";

/** Scope keys join attribute values with `PATH_SEPARATOR`; a value containing it would silently corrupt every derived key, so refuse loudly here. */
export const assertScopeSafeValues = (
  items: readonly StudioItem[],
  draft: Pick<StudioDraft, "spine" | "attrOverrides">,
): void => {
  for (const item of items) {
    const attrs = effectiveAttrs(item, draft);
    for (const level of draft.spine) {
      const value = attrs[level.attr];
      if (value !== null && value !== undefined && value.includes(PATH_SEPARATOR)) {
        throw new Error(
          `Attribute value contains the scope separator ("${PATH_SEPARATOR}"): ` +
            `sku ${item.skuId}, ${level.attr} = "${value}". Scope keys would corrupt; ` +
            "clean the value at its source before re-measuring.",
        );
      }
    }
  }
};

/** Re-measure the four contract maps from `layout`; everything else carries through untouched. A placed plan must yield non-empty `shelfHints` — empty hints select fresh mode. */
export const remeasureDraft = (
  items: readonly StudioItem[],
  layout: GeneratedLayout,
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  options: { bandShareThreshold?: number } = {},
): StudioDraft => {
  assertScopeSafeValues(items, draft);
  const projected = itemsWithLayout(items, layout)
    .map((item) => ({ ...item, attrs: effectiveAttrs(item, draft) }))
    .filter((item) => item.placements.length > 0);

  const annotations: {
    scopedValueOrder: Record<string, string[]>;
    bandOverrides: Record<string, number[]>;
    orientOverrides: Record<string, Orientation>;
  } = { scopedValueOrder: {}, bandOverrides: {}, orientOverrides: {} };
  annotateContract(
    projected,
    draft.spine,
    runs,
    annotations,
    0,
    [],
    options.bandShareThreshold,
    {},
    // Must be the generator's walk; the bare spine keys the maps to a walk it never makes.
    draft.scopedLevelOrder,
  );

  const shelfHints: Record<string, { shelf: number; hFacings: number }[]> = {};
  for (const item of projected) {
    shelfHints[item.skuId] = item.placements.map((placement) => ({
      shelf: placement.shelf,
      hFacings: placement.hFacings,
    }));
  }

  return {
    ...draft,
    scopedValueOrder: annotations.scopedValueOrder,
    bandOverrides: annotations.bandOverrides,
    orientOverrides: annotations.orientOverrides,
    shelfHints,
  };
};
